import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import {
  TrendingDown, TrendingUp, ArrowLeft, AlertTriangle, Calendar,
  ChevronDown, ChevronUp, Filter, Receipt, HelpCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import SiteDisclaimer from '@/components/SiteDisclaimer';
import BackOrHomeButton from '@/components/BackOrHomeButton';
import UnappliedSplitsBanner from '@/components/UnappliedSplitsBanner';
import ModalCloseButton from '@/components/ModalCloseButton';
import { AccountTypeBadge } from '@/components/AccountTypeModal';
import { ACCOUNT_TYPES } from '@/lib/accountTypes';
import { useActivePortfolioId } from '@/components/PortfolioSelector';
import InlineStockModal from '@/components/InlineStockModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tax Lot Optimizer — every buy lot in the user's portfolio shown
// with unrealized G/L (in $ + %), Long-term (>365 days held) vs
// Short-term tagging, and a top summary bar with the dollar amount of
// losses available to harvest in each tax bucket. Lots that are
// loss-eligible AND share a ticker with a sell in the past 30 days are
// flagged with a wash-sale risk badge. NOT financial advice — the bar
// at the top says so explicitly.
//
// Default sort: biggest unrealized losses first (so harvest candidates
// land at the top). Click any column header to re-sort.

const FILTERS = [
  { id: 'all',     label: 'All Lots',
    explain: 'Shows every buy lot in your portfolio. Each row is one separate buy — DiviDrip tracks lots individually so cost basis and "days held" stay accurate even if you bought the same ticker on multiple dates. Robinhood imports automatically split your positions into the same FIFO lots Robinhood itself uses.' },
  { id: 'loss',    label: 'Losses Only',
    explain: 'Shows only lots whose current value is below their cost basis. These are your candidates for tax-loss harvesting — selling them locks in a capital loss that can offset realized gains and (US federal) up to $3,000/yr of ordinary income. Mixed losses and gains in the same ticker are common; this filter shows them lot-by-lot so you don\'t accidentally sell winners.' },
  { id: 'lt',      label: 'Long-Term Only',
    explain: 'Shows only lots held more than 365 days. Long-term lots get preferential US federal tax treatment on gains (0% / 15% / 20% rates depending on income) and long-term losses offset long-term gains first. If a lot is exactly 365 days old it\'s still short-term — the "more than one year" threshold is strict.' },
  { id: 'st',      label: 'Short-Term Only',
    explain: 'Shows only lots held 365 days or less. Short-term gains are taxed as ordinary income (your regular bracket), so they\'re typically the most expensive to realize. Short-term losses are uniquely valuable: they offset ST gains first, which means they can save you money at your top marginal rate. Holding a profitable position to its 1-year-and-1-day mark can meaningfully reduce the tax bill — the Days Held column shows exactly when each lot crosses over.' },
  { id: 'harvest', label: 'Harvest Candidates',
    explain: 'Loss-eligible lots with no wash-sale risk in the past 30 days. The IRS wash-sale rule disallows a loss deduction if you buy back substantially-identical shares within 30 days before OR after the sale, so DiviDrip checks recent sells of the same ticker. Lots flagged with "Wash risk" are excluded from this filter — wait 31+ days from your most recent sell, harvest from a different ticker, or talk to a tax pro about specifics (linked accounts, spousal accounts, and substantially-similar securities all factor in).' },
];

const HELP_TEXTS = {
  held: {
    title: 'Held — days since you bought this lot',
    body: 'The number of calendar days between the lot\'s purchase date and today. The 365-day mark is a hard line for US federal tax purposes: anything held MORE than 365 days qualifies for long-term capital-gains treatment when sold; 365 days or less is short-term. The date below the day count is the original purchase date — for Robinhood imports this is the actual fill date, for manual entries it\'s whatever date you entered when adding the position. If a lot is approaching 366 days, it can be worth waiting before selling — the difference between short-term (ordinary income rates) and long-term (0%/15%/20%) treatment is often huge.',
  },
  term: {
    title: 'Term — Long-Term (LT) vs Short-Term (ST)',
    body: 'Tax classification based on how long you\'ve held the lot. LONG-TERM (LT) means held more than 365 days — gains qualify for preferential US federal rates (0%/15%/20% depending on income), and long-term losses offset long-term gains first. SHORT-TERM (ST) means held 365 days or less — gains are taxed as ordinary income (your regular bracket, often 22-37%), and short-term losses offset short-term gains first (uniquely valuable since ST gains are taxed at top rates). The exact threshold is "more than one year" so a lot purchased exactly 365 days ago is still short-term — wait one more day for LT treatment. State taxes can differ from federal rules; confirm with a tax pro.',
  },
  harvest_badge: {
    title: 'Harvest — eligible tax-loss harvest candidate',
    body: 'This lot is currently at a loss AND no shares of the same ticker were sold in the past 30 days, meaning selling it now would lock in a deductible capital loss without triggering the IRS wash-sale rule. The realized loss offsets capital gains dollar-for-dollar (matched by term first — LT to LT, ST to ST), and any unused loss can offset up to $3,000 of ordinary income per year (US federal). Excess loss carries forward to future years. CAVEATS: wash-sale rules also apply to substantially-identical securities (different ETFs tracking the same index can count), to your spouse\'s and IRA accounts, and to buys IN THE NEXT 30 DAYS — don\'t plan a reentry inside that window. Always confirm specific moves with a tax pro.',
  },
  wash_badge: {
    title: 'Wash risk — recent sell on this ticker',
    body: 'You sold shares of this ticker within the past 30 days. The IRS wash-sale rule disallows the loss deduction if you sell at a loss within 30 days of buying back substantially-identical shares (and vice-versa). Since you just sold this ticker, harvesting more shares now could fall into the disallowed window — the disallowed loss gets added to the cost basis of the replacement shares (it\'s deferred, not erased, but the timing changes). To safely harvest: wait 31+ days from your most recent sell, harvest from a different ticker instead, or reach out to a tax pro. The 30-day window applies to ALL of your accounts (taxable, IRA, spouse\'s) — DiviDrip can only see your own portfolio.',
  },
  total_gl: {
    title: 'Total Unrealized G/L',
    body: 'The sum of (current value − cost basis) across every lot in your portfolio that has a current price cached. Unrealized = the gain or loss exists on paper but you haven\'t sold yet, so it doesn\'t count for taxes until you do. Compare this to your portfolio\'s total cost to see overall performance: a $345 unrealized gain on a $3,433 cost basis is +10%. Lots without a current price (rare — usually new IPOs that haven\'t been cached yet) are excluded so the total isn\'t artificially low.',
  },
};

function pct(v) {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const n = Number(v);
  return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
}
function dollar(v, signed = false) {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const n = Number(v);
  const sign = signed ? (n >= 0 ? '+' : '−') : '';
  return `${sign}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function StatCard({ label, value, sub, accent, testId, hint, onInfo }) {
  return (
    <div
      className={`relative rounded-xl border-2 p-4 ${accent === 'green' ? 'border-emerald-300/60 bg-emerald-50/40 dark:border-emerald-700/50 dark:bg-emerald-950/20'
        : accent === 'red' ? 'border-rose-300/60 bg-rose-50/40 dark:border-rose-700/50 dark:bg-rose-950/20'
        : 'border-border bg-muted/30'}`}
      title={hint}
      data-testid={testId}
    >
      {onInfo && (
        <button
          type="button"
          onClick={onInfo}
          className="absolute top-2 right-2 text-muted-foreground hover:text-cyan-600 dark:hover:text-cyan-400"
          title={`What is "${label}"?`}
          data-testid={`${testId}-info`}
          aria-label={`Explain ${label}`}
        >
          <HelpCircle className="w-3.5 h-3.5" />
        </button>
      )}
      <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{label}</div>
      <div className={`text-2xl font-mono font-bold mt-1 ${accent === 'green' ? 'text-emerald-700 dark:text-emerald-300'
        : accent === 'red' ? 'text-rose-700 dark:text-rose-300'
        : 'text-foreground'}`}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

export default function TaxLots() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Round P108x (Feb 2026) — in-place stock modal. Clicking a ticker
  // used to navigate to `/?view=portfolio&stock=X`, which reloaded
  // the Dashboard behind the modal and dumped the user on the
  // Portfolio page after close. `InlineStockModal` opens the same
  // modal locally so the user stays on /tax-lots.
  const [stockModalTicker, setStockModalTicker] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  // Round 95d (Jun 2026) — deep-linkable filter. The Year-End
  // Snapshot's $3,000 offset meter links here with
  // ?filter=harvest&from=year-end&headroom=X so the page opens
  // pre-filtered to harvest candidates with a context banner.
  const [searchParams] = useSearchParams();
  const urlFilter = searchParams.get('filter');
  const [filter, setFilter] = useState(
    ['all', 'loss', 'lt', 'st', 'harvest'].includes(urlFilter) ? urlFilter : 'all'
  );
  const fromYearEnd = searchParams.get('from') === 'year-end';
  const headroomParam = Number(searchParams.get('headroom')) || 0;
  // Account-type filter — separate axis from the LT/ST/harvest filter.
  // 'all' = show every account; any of the 6 keys narrows to that one.
  // Round 92o++++++++c (Feb 2026) — added so tax-prep users can
  // isolate, e.g., only their Taxable lots when computing NIIT.
  const [acctFilter, setAcctFilter] = useState('all');
  const [sortKey, setSortKey] = useState('gain_loss');
  const [sortDir, setSortDir] = useState('asc'); // losses-first by default
  // Pop-out explainer state. `info` shape: { title, body }. The modal
  // is rendered at the bottom of the tree with mobile-safe sizing
  // (max-h-[85vh], inner overflow-y-auto, scaled padding) — same
  // pattern StockModal uses so it never falls off-screen on phones.
  const [info, setInfo] = useState(null);

  // Round P108r (Feb 2026) — scope Tax Lots to the currently-active
  // portfolio. Previously the endpoint returned every lot the user
  // owned user-wide, so a Plus user viewing "Test 2" (2 lots) saw
  // 18 lots on Tax Lots including phantom tickers from sibling
  // portfolios. Now the frontend sends `?portfolio_id=` and the
  // backend scopes the query. Re-fetches when the user flips
  // the dropdown.
  const { activeId: activePortfolioId } = useActivePortfolioId();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/portfolio/tax-lots`, {
          params: activePortfolioId ? { portfolio_id: activePortfolioId } : {},
          withCredentials: true,
        });
        if (!cancelled) setData(data);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.detail || err?.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activePortfolioId, refreshTick]);

  const filteredLots = useMemo(() => {
    if (!data?.lots) return [];
    const lots = data.lots.filter(l => {
      // Tax-status axis
      if (filter === 'loss' && !l.is_loss) return false;
      if (filter === 'lt' && l.term !== 'LT') return false;
      if (filter === 'st' && l.term !== 'ST') return false;
      if (filter === 'harvest' && !(l.is_loss && !l.wash_sale_risk)) return false;
      // Account-type axis (intersection — both filters must match)
      if (acctFilter !== 'all' && (l.account_type || 'taxable') !== acctFilter) return false;
      // Round 92o++++++++e (Feb 2026) — when the user is in
      // "Harvest Candidates" mode, automatically hide lots inside
      // tax-advantaged accounts (Roth, Trad IRA, 401(k), HSA).
      // Capital losses inside those accounts are NOT deductible for
      // federal tax purposes — surfacing them would actively mislead
      // a user trying to harvest. The explainer banner above the table
      // tells them this is happening so they can override with the
      // account filter if they really want to see them.
      if (filter === 'harvest') {
        const acct = l.account_type || 'taxable';
        const NIIT_APPLIES = new Set(['taxable', 'other']);
        if (!NIIT_APPLIES.has(acct)) return false;
      }
      return true;
    });
    const sorted = [...lots].sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      // Push nulls to the bottom regardless of direction so missing
      // prices don't sort to the top.
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'string') {
        return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      return sortDir === 'asc' ? va - vb : vb - va;
    });
    return sorted;
  }, [data, filter, acctFilter, sortKey, sortDir]);

  const onSort = (key) => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(key === 'gain_loss' || key === 'gain_loss_pct' ? 'asc' : 'desc'); }
  };

  const SortIcon = ({ k }) => {
    if (sortKey !== k) return null;
    return sortDir === 'asc' ? <ChevronUp className="w-3 h-3 inline" /> : <ChevronDown className="w-3 h-3 inline" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen min-h-[100svh] flex items-center justify-center" data-testid="tax-lots-loading">
        <div className="text-sm text-muted-foreground">Loading your tax lots…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen min-h-[100svh] flex items-center justify-center" data-testid="tax-lots-error">
        <div className="text-sm text-rose-600">Couldn't load: {error}</div>
      </div>
    );
  }

  const summary = data?.summary || {};
  const totalGl = summary.total_gain_loss ?? 0;
  const totalGlAccent = totalGl >= 0 ? 'green' : 'red';
  const harvestSubtotal = (summary.lt_loss_available || 0) + (summary.st_loss_available || 0);

  return (
    <div className="min-h-screen min-h-[100svh] bg-background text-foreground">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 pt-6 pb-0">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 gap-2">
          <div className="flex items-center gap-3">
            <BackOrHomeButton testid="tax-lots-back-or-home-btn" showEscHint={false} />
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
                <Receipt className="w-7 h-7 text-cyan-600 dark:text-cyan-400" />
                Tax Lot Optimizer
              </h1>
              <p className="text-xs text-muted-foreground mt-0.5">
                Every buy lot in your portfolio with unrealized G/L, term classification, and tax-loss harvest candidates.
              </p>
            </div>
          </div>
          <Link to="/tax-harvest-report" data-testid="tax-lots-report-link">
            <Button variant="outline" size="sm" className="gap-1.5" title="Year-end ledger of every harvest you've already tagged. Print or export for your tax filing.">
              <Receipt className="w-3.5 h-3.5" />
              Year-End Report
            </Button>
          </Link>
        </div>

        {/* Feb 2026 — Warn the user before they trust cost basis on
            this page: any past-execution split that hasn't been
            applied to their lots means shares + cost/share are
            still pre-split and tax numbers are wrong. */}
        <UnappliedSplitsBanner portfolioId={activePortfolioId} />

        {/* NOT financial advice strip — required given the page name */}
        {/* Year-End Snapshot context banner — only when arriving via
            the Realized Gains Report's harvest-candidates link. */}
        {fromYearEnd && (
          <div
            className="rounded-lg border-2 border-emerald-300/60 bg-emerald-50/40 dark:border-emerald-700/50 dark:bg-emerald-950/20 p-3 mb-5 text-xs text-emerald-900 dark:text-emerald-200 flex items-start gap-2"
            data-testid="tax-lots-headroom-banner"
          >
            <Receipt className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              <strong>From your Year-End Snapshot:</strong>{' '}
              {headroomParam > 0
                ? <>you still have <strong>${headroomParam.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong> of this year&apos;s $3,000 ordinary-income offset unfilled.{' '}</>
                : null}
              The <strong>Harvest Candidates</strong> filter below shows your loss lots with no wash-sale
              risk, biggest losses first — harvesting enough of them before Dec 31 turns paper losses
              into a real deduction.
            </span>
          </div>
        )}
        <div className="rounded-lg border border-amber-300/60 bg-amber-50/50 dark:border-amber-700/50 dark:bg-amber-950/20 p-3 mb-5 text-[11px] text-amber-900 dark:text-amber-200" data-testid="tax-lots-disclaimer">
          <strong>Educational view, not tax advice.</strong> Long-term vs short-term classification is based on the date you entered (or that the CSV imported). Federal tax rules can change and individual situations differ — confirm any harvest moves with a tax pro. Wash-sale flags are a heuristic based on sells in the past 30 days; they don't catch substantially-similar securities or sells in linked accounts.
        </div>

        {/* Summary bar */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <StatCard
            label="Total Unrealized G/L"
            value={dollar(totalGl, true)}
            sub={pct(summary.total_gain_loss_pct)}
            accent={totalGlAccent}
            testId="tax-summary-total-gl"
            hint="Sum of (current value − cost) across all lots that have a current price. Excludes lots where the latest price isn't yet cached."
            onInfo={() => setInfo(HELP_TEXTS.total_gl)}
          />
          <StatCard
            label="LT Losses Available"
            value={dollar(summary.lt_loss_available)}
            sub="Held > 365 days"
            accent={summary.lt_loss_available > 0 ? 'red' : 'neutral'}
            testId="tax-summary-lt-losses"
            hint="Total dollar value of unrealized losses on lots held more than 365 days. Selling these locks in a long-term capital loss, which offsets long-term gains first, then up to $3,000/yr of ordinary income (US federal rules — your situation may differ)."
          />
          <StatCard
            label="ST Losses Available"
            value={dollar(summary.st_loss_available)}
            sub="Held ≤ 365 days"
            accent={summary.st_loss_available > 0 ? 'red' : 'neutral'}
            testId="tax-summary-st-losses"
            hint="Total dollar value of unrealized losses on lots held 365 days or less. Short-term losses offset short-term gains first (taxed as ordinary income), making them especially valuable if you have ST gains to offset this year."
          />
          <StatCard
            label="Total Harvestable"
            value={dollar(harvestSubtotal)}
            sub={`${(summary.wash_sale_warnings?.length || 0)} wash-sale flag${(summary.wash_sale_warnings?.length || 0) === 1 ? '' : 's'}`}
            accent={harvestSubtotal > 0 ? 'red' : 'neutral'}
            testId="tax-summary-harvestable"
            hint="LT + ST losses combined. Lots flagged with a wash-sale risk are excluded from the 'Harvest Candidates' filter below — selling those within 30 days of a related buy can disallow the loss for tax purposes."
          />
        </div>

        {/* Wash-sale warnings detail */}
        {(summary.wash_sale_warnings?.length || 0) > 0 && (
          <div className="rounded-lg border border-rose-300/60 bg-rose-50/50 dark:border-rose-700/50 dark:bg-rose-950/20 p-3 mb-5" data-testid="tax-wash-sale-warnings">
            <div className="flex items-center gap-2 text-sm font-bold text-rose-700 dark:text-rose-300">
              <AlertTriangle className="w-4 h-4" />
              Wash-sale risk on {summary.wash_sale_warnings.length} ticker{summary.wash_sale_warnings.length === 1 ? '' : 's'}
            </div>
            <p className="text-[11px] text-rose-900/80 dark:text-rose-200/80 mt-1">
              You sold these tickers within the past 30 days. If you also sell a loss lot on the same ticker in that window, the IRS wash-sale rule can disallow the deduction. Harvest from a different ticker, or wait 31+ days from the most recent sell.
            </p>
            <div className="flex flex-wrap gap-2 mt-2">
              {summary.wash_sale_warnings.map(w => (
                <span key={w.ticker} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-rose-100 dark:bg-rose-950 text-rose-800 dark:text-rose-200" data-testid={`tax-wash-${w.ticker}`}>
                  {w.ticker} · {w.lot_count} lot{w.lot_count === 1 ? '' : 's'} · {dollar(w.total_loss)} loss
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Filter pills — each pill carries a tiny "?" icon that opens
            an info modal explaining what the filter actually means.
            The pill body itself still toggles the filter; the question
            mark is its own button so the two interactions don't fight
            on touch devices. */}
        <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="tax-filter-pills">
          <Filter className="w-3.5 h-3.5 text-muted-foreground" />
          {FILTERS.map(f => {
            const count = !data?.lots ? 0
              : f.id === 'loss' ? data.lots.filter(l => l.is_loss).length
              : f.id === 'lt' ? data.lots.filter(l => l.term === 'LT').length
              : f.id === 'st' ? data.lots.filter(l => l.term === 'ST').length
              : f.id === 'harvest' ? data.lots.filter(l => l.is_loss && !l.wash_sale_risk).length
              : data.lots.length;
            const active = filter === f.id;
            return (
              <div
                key={f.id}
                className={`inline-flex items-center rounded-full border transition-colors ${
                  active
                    ? 'bg-cyan-100 dark:bg-cyan-950 border-cyan-400 text-cyan-700 dark:text-cyan-300'
                    : 'bg-card border-border text-muted-foreground hover:bg-muted'
                }`}
              >
                <button
                  onClick={() => setFilter(f.id)}
                  className="text-xs font-semibold pl-3 pr-1.5 h-8"
                  data-testid={`tax-filter-${f.id}`}
                >
                  {f.label} <span className="font-mono opacity-70">({count})</span>
                </button>
                <button
                  type="button"
                  onClick={() => setInfo({ title: f.label, body: f.explain })}
                  className="pr-2.5 pl-1 h-8 inline-flex items-center text-muted-foreground hover:text-cyan-600 dark:hover:text-cyan-400"
                  title={`What does "${f.label}" mean?`}
                  data-testid={`tax-filter-info-${f.id}`}
                  aria-label={`Explain ${f.label}`}
                >
                  <HelpCircle className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>

        {/* Round 92o++++++++c (Feb 2026) — Account-type filter row.
            Renders only when the user has tagged lots across more than
            one account type (otherwise the row would be redundant). */}
        {(() => {
          if (!data?.lots) return null;
          const accountCounts = {};
          data.lots.forEach(l => {
            const k = l.account_type || 'taxable';
            accountCounts[k] = (accountCounts[k] || 0) + 1;
          });
          const presentTypes = Object.keys(accountCounts);
          if (presentTypes.length < 2) return null;
          return (
            <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="tax-account-filter-pills">
              <Filter className="w-3.5 h-3.5 text-muted-foreground" />
              <button
                type="button"
                onClick={() => setAcctFilter('all')}
                className={`text-xs font-semibold px-3 h-8 rounded-full border transition-colors ${
                  acctFilter === 'all'
                    ? 'bg-cyan-100 dark:bg-cyan-950 border-cyan-400 text-cyan-700 dark:text-cyan-300'
                    : 'bg-card border-border text-muted-foreground hover:bg-muted'
                }`}
                data-testid="tax-acct-filter-all"
              >
                All Accounts <span className="font-mono opacity-70">({data.lots.length})</span>
              </button>
              {ACCOUNT_TYPES.filter(at => accountCounts[at.key]).map(at => {
                const active = acctFilter === at.key;
                const Icon = at.icon;
                return (
                  <button
                    key={at.key}
                    type="button"
                    onClick={() => setAcctFilter(at.key)}
                    className={`inline-flex items-center gap-1 text-xs font-semibold px-3 h-8 rounded-full border transition-colors ${
                      active
                        ? 'bg-cyan-100 dark:bg-cyan-950 border-cyan-400 text-cyan-700 dark:text-cyan-300'
                        : 'bg-card border-border text-muted-foreground hover:bg-muted'
                    }`}
                    data-testid={`tax-acct-filter-${at.key}`}
                    title={at.hint}
                  >
                    <Icon className="w-3 h-3" />
                    {at.short} <span className="font-mono opacity-70">({accountCounts[at.key]})</span>
                  </button>
                );
              })}
            </div>
          );
        })()}

        {/* Round 92o++++++++e (Feb 2026) — Account-aware harvest
            advisory. When the user is in "Harvest Candidates" mode AND
            their portfolio has lots inside tax-advantaged accounts,
            we automatically hide those (since capital losses inside
            Roth/Trad IRA/401(k)/HSA aren't federally deductible) and
            surface this banner so they know what's been filtered out. */}
        {(() => {
          if (filter !== 'harvest' || !data?.lots) return null;
          const NIIT_APPLIES = new Set(['taxable', 'other']);
          const hiddenInsideRetirement = data.lots.filter(l =>
            l.is_loss && !l.wash_sale_risk && !NIIT_APPLIES.has(l.account_type || 'taxable')
          ).length;
          if (hiddenInsideRetirement === 0) return null;
          return (
            <div
              className="mb-3 rounded-md border border-amber-300/60 bg-amber-50/60 dark:border-amber-700/40 dark:bg-amber-950/30 p-3 text-[12px] text-amber-900 dark:text-amber-100"
              data-testid="harvest-retirement-advisory"
            >
              <div className="font-bold mb-0.5">
                Hidden: {hiddenInsideRetirement} lot{hiddenInsideRetirement === 1 ? '' : 's'} inside tax-advantaged accounts
              </div>
              <p className="leading-snug">
                Capital losses inside <strong>Roth IRA, Traditional IRA, 401(k),
                and HSA</strong> are <strong>not deductible</strong> for federal
                taxes — harvesting them won&rsquo;t lower your tax bill. The
                list below shows only your <strong>taxable + other</strong>{' '}
                losses, where harvesting actually works. Use the account
                filter above to see hidden lots anyway.
              </p>
            </div>
          );
        })()}

        {/* Lots table */}
        {filteredLots.length === 0 ? (
          <div className="rounded-lg border border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground font-semibold" data-testid="tax-lots-empty">
            {data?.lots?.length === 0
              ? <>You don't have any portfolio lots yet. <Link to="/" className="text-cyan-600 hover:underline">Add positions</Link> or import a CSV to get started.</>
              : 'No lots match the current filter.'}
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="tax-lots-table-wrap">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <Th label="Ticker" k="ticker" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                    <Th label="Shares" k="shares" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />
                    <Th label="Cost/Sh" k="cost_per_share" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />
                    <Th label="Current" k="current_price" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />
                    <Th label="Cost" k="total_cost" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />
                    <Th label="Value" k="current_value" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />
                    <Th label="G/L" k="gain_loss" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />
                    <Th label="G/L %" k="gain_loss_pct" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />
                    <Th label="Held" k="days_held" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" onInfo={() => setInfo(HELP_TEXTS.held)} />
                    <Th label="Term" k="term" sortKey={sortKey} sortDir={sortDir} onSort={onSort} onInfo={() => setInfo(HELP_TEXTS.term)} />
                    <Th label="Account" k="account_type" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                    <Th label="Tag" k="is_loss" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                  </tr>
                </thead>
                <tbody>
                  {filteredLots.map((l, i) => {
                    const gl = l.gain_loss;
                    const isLoss = l.is_loss;
                    return (
                      <tr key={l.position_id || i} className={`border-t border-border odd:bg-muted/20 ${isLoss ? 'bg-rose-50/30 dark:bg-rose-950/10' : ''}`} data-testid={`tax-lot-row-${l.ticker}-${i}`}>
                        <td className="px-3 py-2 font-mono font-bold">
                          <button
                            type="button"
                            onClick={() => setStockModalTicker(l.ticker)}
                            className="text-cyan-600 dark:text-cyan-400 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/40 rounded"
                            data-testid={`tax-lot-ticker-btn-${l.ticker}-${i}`}
                          >
                            {l.ticker}
                          </button>
                          {l.name && <div className="text-[10px] text-muted-foreground font-sans truncate max-w-[200px]">{l.name}</div>}
                          {l.is_treasury_vehicle && (
                            <div
                              className="text-[9px] font-sans font-semibold text-indigo-600 dark:text-indigo-400 cursor-help"
                              title="This is a treasury-vehicle preferred. Its distributions are Return of Capital — each payout lowers this lot's cost basis instead of being taxed as dividend income. Your broker's 1099 basis will drift below what's shown here. Not applicable inside IRAs."
                              data-testid={`tax-lot-roc-note-${l.ticker}-${l.position_id || i}`}
                            >
                              ROC — lowers cost basis
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{l.shares?.toFixed(4)}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{dollar(l.cost_per_share)}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{dollar(l.current_price)}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{dollar(l.total_cost)}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{dollar(l.current_value)}</td>
                        <td className={`px-3 py-2 text-right font-mono text-xs font-bold ${gl == null ? 'text-muted-foreground' : gl >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                          {gl == null ? '—' : <>
                            {gl >= 0 ? <TrendingUp className="w-3 h-3 inline mr-0.5" /> : <TrendingDown className="w-3 h-3 inline mr-0.5" />}
                            {dollar(gl, true)}
                          </>}
                        </td>
                        <td className={`px-3 py-2 text-right font-mono text-xs ${gl == null ? 'text-muted-foreground' : gl >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>{pct(l.gain_loss_pct)}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs text-muted-foreground">
                          <Calendar className="w-3 h-3 inline mr-0.5" />
                          {l.days_held}d
                          {l.date_purchased && <div className="text-[10px]">{l.date_purchased.slice(0,10)}</div>}
                        </td>
                        <td className="px-3 py-2">
                          <button
                            type="button"
                            onClick={() => setInfo(HELP_TEXTS.term)}
                            className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold cursor-pointer hover:ring-2 hover:ring-cyan-400/40 transition-shadow ${l.term === 'LT' ? 'bg-cyan-100 dark:bg-cyan-950 text-cyan-700 dark:text-cyan-300' : 'bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-300'}`}
                            title={`What does ${l.term} mean? Click for details.`}
                          >
                            {l.term}
                          </button>
                        </td>
                        <td className="px-3 py-2" data-testid={`tax-lot-account-${l.position_id}`}>
                          <AccountTypeBadge value={l.account_type || 'taxable'} size="xs" />
                        </td>
                        <td className="px-3 py-2">
                          {l.wash_sale_risk ? (
                            <button
                              type="button"
                              onClick={() => setInfo(HELP_TEXTS.wash_badge)}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-rose-100 dark:bg-rose-950 text-rose-700 dark:text-rose-300 cursor-pointer hover:ring-2 hover:ring-rose-400/40 transition-shadow"
                              title="Click for wash-sale rule details"
                            >
                              <AlertTriangle className="w-3 h-3" />
                              Wash risk
                            </button>
                          ) : isLoss ? (
                            <button
                              type="button"
                              onClick={() => setInfo(HELP_TEXTS.harvest_badge)}
                              className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 cursor-pointer hover:ring-2 hover:ring-emerald-400/40 transition-shadow"
                              title="Click to learn about tax-loss harvesting"
                            >
                              Harvest
                            </button>
                          ) : (
                            <span className="text-[10px] text-muted-foreground">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="px-3 py-2 text-[11px] text-muted-foreground border-t border-border bg-muted/20" data-testid="tax-lots-row-footer">
              Showing <span className="font-mono font-bold text-foreground">{filteredLots.length}</span> of {data?.lots?.length || 0} lot{(data?.lots?.length || 0) === 1 ? '' : 's'}
            </div>
          </div>
        )}
      </div>

      <SiteDisclaimer />

      <InfoModal
        open={!!info}
        onClose={() => setInfo(null)}
        title={info?.title}
        body={info?.body}
      />

      {/* Round P108x — in-place StockModal so ticker clicks stay on
          /tax-lots. Bumps refreshTick after a portfolio-changing
          action (buy/sell/DRIP inside the modal) so the lots table
          refetches and the row reflects new cost basis / gain. */}
      <InlineStockModal
        ticker={stockModalTicker}
        onClose={() => setStockModalTicker(null)}
        onPortfolioChanged={() => setRefreshTick((t) => t + 1)}
      />
    </div>
  );
}

function Th({ label, k, sortKey, sortDir, onSort, align, onInfo }) {
  const active = sortKey === k;
  return (
    <th
      className={`px-3 py-2 text-[11px] uppercase tracking-wider font-bold ${align === 'right' ? 'text-right' : 'text-left'} ${active ? 'text-cyan-600 dark:text-cyan-400' : 'text-muted-foreground'}`}
    >
      <span
        onClick={() => onSort(k)}
        className="cursor-pointer select-none hover:text-cyan-600 dark:hover:text-cyan-400"
      >
        {label}
        {active && (sortDir === 'asc' ? <ChevronUp className="w-3 h-3 inline ml-0.5" /> : <ChevronDown className="w-3 h-3 inline ml-0.5" />)}
      </span>
      {onInfo && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onInfo(); }}
          className="ml-1 text-muted-foreground/70 hover:text-cyan-600 dark:hover:text-cyan-400 align-middle"
          title={`What does "${label}" mean?`}
          aria-label={`Explain ${label}`}
        >
          <HelpCircle className="w-3 h-3 inline" />
        </button>
      )}
    </th>
  );
}

// Mobile-safe info modal — same pattern as StockModal: fixed inset-0
// with backdrop, centered card capped at max-h-[85vh], inner panel
// scrolls if content overflows. Padding scales down on phones (p-3
// vs sm:p-5). Closes on backdrop click, Escape, or the X button.
function InfoModal({ open, onClose, title, body }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-3 sm:p-4"
      onClick={onClose}
      data-testid="tax-info-modal-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md max-h-[85vh] bg-card border rounded-xl shadow-2xl overflow-hidden flex flex-col"
        data-testid="tax-info-modal"
      >
        <div className="flex items-start justify-between gap-2 px-4 py-3 sm:px-5 border-b bg-muted/30">
          <h3 className="text-sm sm:text-base font-bold pr-6 leading-snug" data-testid="tax-info-modal-title">{title}</h3>
          <ModalCloseButton onClick={onClose} testId="tax-info-modal-close" />
        </div>
        <div className="overflow-y-auto p-4 sm:p-5 text-[13px] sm:text-sm leading-relaxed text-foreground/90 pb-24 sm:pb-5" data-testid="tax-info-modal-body">
          {body}
        </div>
        <div className="px-4 sm:px-5 py-2 border-t bg-muted/20 text-[11px] text-muted-foreground">
          Educational only — not tax advice. Confirm specific moves with a tax pro.
        </div>
      </div>
    </div>
  );
}
